import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { TarotReading } from '../types/tarot';

// apiClient interceptor returns response.data directly (not AxiosResponse)
// so we cast the result to any to access the unwrapped API body
async function fetchDeepDives(parentReadingId: string): Promise<TarotReading[]> {
  const response = (await apiClient.get(
    `/api/readings/${parentReadingId}/deep-dives`
  )) as any;

  // response IS the API body: { success, data: { parentReadingId, deepDives, count } }
  if (!response.success) {
    throw new Error('Failed to fetch deep dives');
  }

  return (response.data.deepDives as any[]).map((dd) => ({
    id: dd.id,
    userId: '',
    question: dd.question,
    spreadId: dd.readingType || 'deep_dive',
    cards: typeof dd.cardsDrawn === 'string'
      ? JSON.parse(dd.cardsDrawn)
      : (dd.cardsDrawn ?? []),
    interpretation: dd.aiPrediction,
    aiInterpretation: dd.aiPrediction,
    isFavorite: dd.isFavorite || false,
    isPrivate: false,
    status: dd.status,
    tags: [],
    parentReadingId: dd.parentReadingId,
    createdAt: dd.createdAt,
    updatedAt: dd.updatedAt
  }));
}

/**
 * Hook to fetch deep dives for a parent reading
 */
export function useDeepDives(parentReadingId: string | null) {
  return useQuery({
    queryKey: ['deep-dives', parentReadingId],
    queryFn: () => parentReadingId ? fetchDeepDives(parentReadingId) : Promise.resolve([]),
    enabled: !!parentReadingId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Helper function to load deep dives without using the hook
 */
export async function loadDeepDives(parentReadingId: string): Promise<TarotReading[]> {
  return fetchDeepDives(parentReadingId);
}
